import { gql } from '@apollo/client';
import { AGE_GROUP_FIELDS } from './age-group.queries';

// The identity of an ice time as other domains cite it — enough to say *which*
// ice time, not to render its roster. Consumed by the communication surfaces.
export const ASSESSMENT_SESSION_SUMMARY_FIELDS = gql`
  fragment AssessmentSessionSummaryFields on AssessmentSession {
    _id
    date
    start
    duration
    location
    area
    address
  }
`;

// Double-bookings on one sheet of ice. Selected here so both the list rows and the
// detail form can warn without a second round trip.
export const ICE_SHEET_CONFLICT_FIELDS = gql`
  fragment IceSheetConflictFields on IceSheetConflict {
    _id
    date
    start
    duration
    location
    area
  }
`;

export const ASSESSMENT_SESSION_FIELDS = gql`
  fragment AssessmentSessionFields on AssessmentSession {
    _id
    location
    area
    address
    date
    start
    duration
    gender
    assessmentId
    isAttendanceRequested
  }
`;

// The full list-row shape. Any mutation that splices a session into a cached
// list must return this, or the list query's cache read goes incomplete.
export const ASSESSMENT_SESSION_LIST_FIELDS = gql`
  fragment AssessmentSessionListFields on AssessmentSession {
    ...AssessmentSessionFields
    isPlayerNotificationEnabled
    iceConflicts {
      ...IceSheetConflictFields
    }
    playerCount
    drillCount
    practicePlanCount
    ageGroup {
      ...AgeGroupFields
    }
    unappliedActionsCount
    attendanceCounts {
      attending
      pending
      notAttending
      total
    }
  }

  ${ASSESSMENT_SESSION_FIELDS}
  ${ICE_SHEET_CONFLICT_FIELDS}
  ${AGE_GROUP_FIELDS}
`;

// A session's iceConflicts and a stage's isReady are both computed across the whole
// schedule, so any change to one ice time can change what these report for the others.
export const SESSION_SCHEDULE_REFETCH_QUERIES = [
  'GetAssessmentSessionList',
  'GetEditAssessmentSession',
  'GetManageAgeGroupGraphQL',
  'GetManageAgeGroups',
  'GetApproveGroupings',
];

export const CREATE_ASSESSMENT_SESSIONS = gql`
  mutation CreateAssessmentSessions($assessmentId: ID!, $rows: [CreateAssessmentSessionInput!]!) {
    createAssessmentSessions(assessmentId: $assessmentId, rows: $rows) {
      ...AssessmentSessionListFields
    }
  }

  ${ASSESSMENT_SESSION_LIST_FIELDS}
`;

export const REMOVE_ASSESSMENT_SESSION = gql`
  mutation RemoveAssessmentSession($_id: ID!, $notify: Boolean!, $operationId: String) {
    removeAssessmentSession(_id: $_id, notify: $notify, operationId: $operationId) {
      _id
    }
  }
`;

export const RESTORE_ASSESSMENT_SESSION = gql`
  mutation RestoreAssessmentSession($_id: ID!) {
    restoreAssessmentSession(_id: $_id) {
      ...AssessmentSessionListFields
    }
  }

  ${ASSESSMENT_SESSION_LIST_FIELDS}
`;

export const CLOSE_ASSESSMENT_SESSION = gql`
  mutation CloseAssessmentSession($_id: ID!) {
    closeAssessmentSession(_id: $_id) {
      ...AssessmentSessionListFields
    }
  }

  ${ASSESSMENT_SESSION_LIST_FIELDS}
`;

export const OPEN_ASSESSMENT_SESSION = gql`
  mutation OpenAssessmentSession($_id: ID!) {
    openAssessmentSession(_id: $_id) {
      ...AssessmentSessionListFields
    }
  }

  ${ASSESSMENT_SESSION_LIST_FIELDS}
`;

export const ASSIGN_JERSEY = gql`
  mutation assignJersey($_id: ID!, $playerAssessmentId: ID!, $jersey: Jersey) {
    assignJersey(_id: $_id, playerAssessmentId: $playerAssessmentId, jersey: $jersey) {
      _id
      jerseys
    }
  }
`;

export const UNASSIGN_JERSEY = gql`
  mutation unassignJersey($_id: ID!, $playerAssessmentId: ID!) {
    unassignJersey(_id: $_id, playerAssessmentId: $playerAssessmentId) {
      _id
      jerseys
    }
  }
`;

export const ADD_CHECKED_IN_PLAYER = gql`
  mutation addCheckedInPlayer($_id: ID!, $playerAssessmentId: ID!) {
    addCheckedInPlayer(_id: $_id, playerAssessmentId: $playerAssessmentId) {
      _id
      checkedInPlayers {
        _id
      }
    }
  }
`;

export const REMOVE_CHECKED_IN_PLAYER = gql`
  mutation removeCheckedInPlayer($_id: ID!, $playerAssessmentId: ID!) {
    removeCheckedInPlayer(_id: $_id, playerAssessmentId: $playerAssessmentId) {
      _id
      checkedInPlayers {
        _id
      }
    }
  }
`;

export const COUNT_CLOSED_ASSESSMENT_SESSIONS = gql`
  query CountClosedAssessmentSessions($assessmentId: ID!, $ageGroupIds: [ID!]!) {
    countClosedAssessmentSessions(assessmentId: $assessmentId, ageGroupIds: $ageGroupIds)
  }
`;


export const GET_ARCHIVED_ASSESSMENT_SESSION_LIST = gql`
  query GetArchivedAssessmentSessionList($assessmentId: ID!, $ageGroupIds: [ID!]!) {
    archivedAssessmentSessions(assessmentId: $assessmentId, ageGroupIds: $ageGroupIds) {
      ...AssessmentSessionFields
      ageGroup {
        ...AgeGroupFields
      }
    }
  }

  ${ASSESSMENT_SESSION_FIELDS}
  ${AGE_GROUP_FIELDS}
`;

export const GET_CLOSED_ASSESSMENT_SESSION_LIST = gql`
  query GetClosedAssessmentSessionList($assessmentId: ID!, $ageGroupIds: [ID!]!) {
    closedAssessmentSessions(assessmentId: $assessmentId, ageGroupIds: $ageGroupIds) {
      ...AssessmentSessionFields
      playerCount
      drillCount
      practicePlanCount
      ageGroup {
        ...AgeGroupFields
      }
      unappliedActionsCount
    }
  }

  ${ASSESSMENT_SESSION_FIELDS}
  ${AGE_GROUP_FIELDS}
`;

export const GET_ASSESSMENT_SESSION_LIST = gql`
  query GetAssessmentSessionList($assessmentId: ID!, $ageGroupIds: [ID!]!) {
    assessmentSessions(assessmentId: $assessmentId, ageGroupIds: $ageGroupIds) {
      ...AssessmentSessionListFields
    }
  }

  ${ASSESSMENT_SESSION_LIST_FIELDS}
`;

export const COUNT_ARCHIVED_ASSESSMENT_SESSIONS = gql`
  query CountArchivedAssessmentSessions($assessmentId: ID!, $ageGroupIds: [ID!]!) {
    countArchivedAssessmentSessions(assessmentId: $assessmentId, ageGroupIds: $ageGroupIds)
  }
`;
